import React, { ReactNode, useEffect, useState } from 'react';
import { IonBadge, IonButton, IonCard, IonCol, IonContent, IonFooter, IonGrid, IonRow, IonText } from '@ionic/react';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel.ts';
import { CheckmarkIcon, IIconComponent } from '../components/icons/Icons.tsx';
import { ITriggerPaywallSource, PaywallDialogViewModel } from './PaywallDialogViewModel.tsx';
import { StorageProvider } from '../support/StorageProvider.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { useAsyncEffect } from '../support/hooks/useAsyncEffect.tsx';
import { NudgeBanner } from '../components/nudge/NudgeBanner.tsx';
import { PaymentTermButton } from './PaymentTermsButton.tsx';
import { NewFeatureBadge } from '../components/badge/NewFeatureBadge.tsx';
import { AutoPylotPlus } from './revenue-cat/AutoPylotPlus.tsx';
import { useIsNative } from '../support/hooks/useIsNative.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { formatElementAsText } from '../support/helpers/format.ts';

type IMessageState = 'trial' | 'downgraded' | 'weather' | 'search-weather' | 'export' | 'fleet' | 'recents' | 'default';

const getMessageState = (params: { triggerSource?: ITriggerPaywallSource, isEligibleForTrial: boolean, hasUpgraded: boolean, hasDowngraded?: boolean }): IMessageState => {
    const triggerToMessage: {[source in ITriggerPaywallSource]: IMessageState} = {
        'new-user': 'default', // should not happen, we use NewUserDialogContent instead
        'menu button': 'default',
        'subscription expired': 'default',
        'weather summary': 'weather',
        'nudge': 'weather',
        'windy': 'weather',
        'bulk-export': 'export',
        'mission-export': 'export',
        'log entry': 'fleet',
        'equipment card': 'fleet',
        'equipment detail': 'fleet',
        'equipment default': 'fleet',
        'recent missions': 'recents',
        'search weather': 'search-weather'
    };
    if (params.hasDowngraded){
        return 'downgraded';
    } else if (params.isEligibleForTrial && !params.hasUpgraded){
        return 'trial';
    } else {
        return triggerToMessage[params.triggerSource || 'menu button'];
    }
};

const DialogHeading: React.FC<{messageState: IMessageState, fontSize: number, trialDays: number}> = ({ messageState, fontSize, trialDays }) => (
    <h1 style={{fontWeight: 'bold', fontSize, marginTop: 0, marginBottom: 0, marginRight: 25}}>
        {messageState === 'default' && (
            <>Enhance flight planning with <AutoPylotPlus /></>
        )}
        {messageState === 'trial' && (
            <>Get full access to <AutoPylotPlus /> <IonText color="primary">for {trialDays} days, on us!</IonText></>
        )}
        {messageState === 'downgraded' && (
            <>It looks like you no longer have <AutoPylotPlus /></>
        )}
        {messageState === 'export' && (
            <>Easily share and export your flights with <AutoPylotPlus /></>
        )}
        {messageState === 'weather' && (
            <>Find the best times to fly with <AutoPylotPlus /></>
        )}
        {messageState === 'fleet' && (
            <>Track drone stats and logs with <AutoPylotPlus /></>
        )}
        {messageState === 'recents' && (
            <>Recreate past missions instantly with <AutoPylotPlus /></>
        )}
        {messageState === 'search-weather' && (
            <>See live weather in search with <AutoPylotPlus /></>
        )}
    </h1>
);

const SectionHeading: React.FC<{children: ReactNode}> = ({ children }) => (
    <b><p style={{marginTop: 20, marginBottom: 20, textAlign: 'center', fontSize: 18}}><b>{children}</b></p></b>
);

const Benefit: React.FC<{Icon?: IIconComponent, children: React.ReactNode; featureKey?: string}> = ({ Icon = CheckmarkIcon, children, featureKey }) => {
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            <div style={{width: 30, textAlign: 'center'}}><Icon strokeWidth={60} size="medium" color="primary" /></div>
            <IonText color="dark" style={{fontSize: 15, marginRight: -20, width: '100%' }}>
                {children}
            </IonText>
            {featureKey && (
                <NewFeatureBadge featureKey={featureKey} />
            )}
        </div>
    );
};

const Benefits: React.FC<{showBadge?: boolean}> = ({ showBadge }) => (
    <div style={{marginTop: 10, marginBottom: 10}}>
        {showBadge &&
            <div style={{position: 'relative', marginBottom: -10, zIndex: 1, display: 'flex', justifyContent: 'center'}}>
                <IonBadge color="primary" style={{ padding: '4px 12px'}}>
                    Why join AutoPylot+?
                </IonBadge>
            </div>
        }
        <IonCard style={{'--color': 'unset', margin: 0, paddingTop: 20}} className="ion-padding">
            <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: '100%'}}>
                <Benefit featureKey="recents">Recreate past missions</Benefit>
                <Benefit featureKey="search-weather">Weather in search menu</Benefit>
                <Benefit>Unlimited flight planning weather</Benefit>
                <Benefit>24-hour & 10-day flight forecasts</Benefit>
                <Benefit>Real-time Windy map layers</Benefit>
                <Benefit>In-depth Weather charts</Benefit>
                <Benefit>Share a PDF of your flight</Benefit>
                <Benefit>Auto-assign drones to flights</Benefit>
                <Benefit>Log and track drone activity</Benefit>
            </div>
        </IonCard>
    </div>
);

export const StandardDialogContent: React.FC<{onUpgrade: () => void, onRestore: () => void}> = ({ onUpgrade, onRestore }) => {
    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const isNative = useIsNative();

    const storageProvider = useInstance(StorageProvider);

    const {isBelowBreakpoint} = useBreakpoints();

    const tracking = useInstance(TrackingService);

    const [previouslyHadPlus, setPreviouslyHadPlus] = useState<boolean>(false);
    useAsyncEffect(async () => {
        const hasPlusStorageKey = 'has-autopylot-plus';
        if (revenueCat.hasUpgraded()) {
            storageProvider.getAsyncBooleanProvider(hasPlusStorageKey).set(true);
            setPreviouslyHadPlus(false);
        } else {
            if (await storageProvider.getAsyncBooleanProvider(hasPlusStorageKey).get()) {
                storageProvider.getAsyncBooleanProvider(hasPlusStorageKey).remove();
                setPreviouslyHadPlus(true);
                dialogVM.open({triggerSource: 'subscription expired'});
            }
        }
    }, [revenueCat, storageProvider]);

    const meta = revenueCat.getOfferMeta().paywallInfo;

    const monthlyPriceString = meta?.monthlyPriceString || '';
    const annualPriceString = (!isNative && meta?.annualPriceStringWeb) ? meta?.annualPriceStringWeb : meta?.annualPriceString || '';
    const annualDiscountBadge = (!isNative && meta?.annualDiscountBadgeWeb) ? meta?.annualDiscountBadgeWeb : meta?.annualDiscountBadge;

    const trialDays = revenueCat.getTrialDays();
    const isEligibleForTrial = revenueCat.isEligibleForTrial();
    const trialPrefix = (isEligibleForTrial ? `${trialDays} days free, then ` : '');

    const annualPricePerMonth = Math.round(Number(annualPriceString?.substring(1))/12*100)/100;
    const annualMessage = `${trialPrefix}${annualPriceString}/yr`;
    const annualSubtext = `${trialPrefix}$${annualPricePerMonth} per month, billed annually`;

    const monthlyMessage = `${trialPrefix}${monthlyPriceString}/mo`;
    const monthlySubtext = `${trialPrefix}${monthlyPriceString}, billed monthly`;

    const trialButtonText = (meta?.buttonText || '').replace(/\d+ days/gi, `${trialDays} days`);
    const continueButtonText = (isEligibleForTrial ? trialButtonText : meta?.buttonTextNoTrial) || 'Continue';

    const term = dialogVM.state.paymentTerm;
    const hasUpgraded = revenueCat.hasUpgraded();
    const hasDowngraded = previouslyHadPlus && !hasUpgraded;

    const messageState = getMessageState({ triggerSource: dialogVM.getTriggerSource(), isEligibleForTrial, hasUpgraded, hasDowngraded });
    const heading = <DialogHeading messageState={messageState} fontSize={isBelowBreakpoint('sm') ? 22 : 30} trialDays={trialDays} />;

    useEffect(() => {
        if (!revenueCat.hasUpgraded()) {
            tracking.track('Paywall Opened', () => ({
                from: dialogVM.getTriggerSource(),
                paywallTitle: formatElementAsText(heading),
                annualPrice: annualPriceString.substring(1),
                monthlyPrice: monthlyPriceString.substring(1),
                buttonText: continueButtonText
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onContinue = () => {
        tracking.track('Paywall Continued', () => ({
            term,
            from: dialogVM.getTriggerSource(),
            paywallTitle: formatElementAsText(heading),
            annualPrice: annualPriceString.substring(1),
            monthlyPrice: monthlyPriceString.substring(1),
            buttonText: continueButtonText
        }));
        onUpgrade();
    };

    return (
        <>
            <IonContent>
                <div style={{margin: 'calc(var(--ion-safe-area-top) + 45px) 20px 0'}}>

                    {heading}

                    {hasDowngraded && (
                        <div style={{marginTop: 20}}>
                            <NudgeBanner
                                color="secondary"
                                excludeIcon={true}
                                data-type="previous-plus-badge"
                                message={
                                    <div style={{textAlign: 'center'}}>
                                        If you didn’t mean to unsubscribe or want to regain access to your AutoPylot+ features, renew your subscription!
                                    </div>
                                }
                            />
                        </div>
                    )}

                    {hasUpgraded && (
                        <>
                            <div style={{marginTop: 40, marginBottom: 20}}>
                                <SectionHeading>You're getting all these benefits!</SectionHeading>
                            </div>

                            <Benefits />

                            <div style={{position: 'absolute',  bottom: '5%', marginLeft: -20, width: '100%', textAlign: 'center'}}>
                                {revenueCat.canManageSubscription() &&
                                    <IonButton color="dark" disabled={dialogVM.state.isWorking} fill="clear" onClick={revenueCat.manageSubscription}>Manage Subscription</IonButton>
                                }
                            </div>
                        </>
                    )}

                    {!hasUpgraded && (
                        <IonGrid style={{ padding: 0 }}>
                            <IonRow class="ion-align-items-flex-start ion-justify-content-between" style={{ padding: 0 }}>

                                <IonCol size="12" sizeMd="5">

                                    <p style={{fontSize: 14}}>Select a plan</p>

                                    <PaymentTermButton
                                        disabled={dialogVM.state.isWorking}
                                        isActive={term === 'annual'}
                                        onClick={() => dialogVM.setState({ paymentTerm: 'annual' })}
                                        termLabel={`Yearly Plan - ${annualPriceString}`}
                                        termDescription={annualSubtext}
                                        badge={(annualDiscountBadge || '').toUpperCase()}
                                    />

                                    <PaymentTermButton
                                        disabled={dialogVM.state.isWorking}
                                        isActive={term === 'monthly'}
                                        onClick={() => dialogVM.setState({ paymentTerm: 'monthly' })}
                                        termLabel={`Monthly Plan - ${monthlyPriceString}`}
                                        termDescription={monthlySubtext}
                                    />
                                </IonCol>

                                <IonCol size="12" sizeMd="7">
                                    <Benefits showBadge />
                                </IonCol>

                            </IonRow>
                        </IonGrid>
                    )}
                </div>
            </IonContent>

            <IonFooter>
                {!hasUpgraded &&
                    <div style={{margin: '20px 30px calc(var(--ion-safe-area-bottom))'}}>

                        <div style={{width: '100%', textAlign: 'center', marginTop: -10}}>
                            <IonText color="medium"><small>{term === 'annual' ? annualMessage : monthlyMessage}</small></IonText>
                        </div>

                        <IonButton
                            disabled={dialogVM.state.isWorking}
                            expand="block"
                            fill="solid"
                            color="primary"
                            onClick={() => onContinue()}
                            data-id="continue"
                        >
                            <span>{continueButtonText}</span>
                        </IonButton>

                        <div style={{width: '100%', textAlign: 'center', marginTop: 10}}>
                            <IonText color="medium">Cancel any time, we'll remind you</IonText>
                        </div>

                        <div style={{width: '100%', maxWidth: 260, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <IonButton disabled={dialogVM.state.isWorking} size="small" expand="block" fill="clear" color="secondary" href="https://www.autopylot.io/terms" target="_blank">Terms of Use</IonButton>
                            {revenueCat.canRestore() &&
                                <IonButton expand="block" size="small" fill="clear" color="secondary" onClick={onRestore} data-id="restore">Restore Purchases</IonButton>
                            }
                        </div>

                    </div>
                }
            </IonFooter>
        </>
    );
};
